import { el, clear, toast } from '../core/dom.js';
import { activeHabits, getState, planFor } from '../core/state.js';
import {
  consumptionMethods, habits, plans, planStates, moneyModels, purchaseTemplates,
  settings as settingsStore, metaSet,
} from '../storage/db.js';
import { costPerUse, currencySymbol } from '../progress/money.js';
import { formatDuration, formatHm, nowIso } from '../core/time.js';
import { newWeeklyCap } from '../rules/hybrid-engine.js';
import { safetyFor } from '../copy/safety.js';
import { containsCrisisLanguage } from '../copy/crisis.js';
import { CATEGORY_TO_DOMAIN, DEFAULT_METHODS_BY_DOMAIN, buildMethodFromSeed } from '../domain/default-methods.js';
import { uuid } from '../core/ids.js';
import { currentRoute, navigate } from '../router.js';
import {
  EXCEPTION_OPTIONS, effectiveLifecycle, graduatePlan, lifecycleLabel,
  logPlanException, moveToMaintenance, pausePlan, resumePlan,
} from '../plans/lifecycle.js';
import { suggestPlanAdjustment } from '../plans/adaptation.js';
import { habitDisplayLabel } from '../core/display.js';
import { events as eventsStore } from '../storage/db.js';
import type { PlanExceptionKind } from '../core/types.js';
import type {
  HabitRecord, IntervalState, MethodLogMode, MoneyModelRecord,
  Pace, PlanRecord, PlanState, PlanStateRecord, PurchaseTemplateRecord, QuotaState,
  SavingsMode, SlipStyle, WindowState,
} from '../core/types.js';

export async function renderPlan(parent: HTMLElement) {
  clear(parent);
  const st = getState();
  const habitsList = activeHabits(st);
  if (habitsList.length === 0) {
    parent.appendChild(el('p', {}, 'No active plan.'));
    return;
  }

  const params = currentRoute().params;
  const requested = params.get('habit');
  const habit = habitsList.find((h) => h.id === requested) ?? st.activeHabit ?? habitsList[0];
  if (!habit) {
    parent.appendChild(el('p', {}, 'No active plan.'));
    return;
  }
  const { plan, planState } = planFor(habit.id, st);
  if (!plan || !planState) {
    parent.appendChild(el('p', {}, 'Plan missing.'));
    return;
  }

  parent.appendChild(habitSwitcher(habitsList, habit));

  const safety = safetyFor(habit.category);
  if (safety.short) {
    parent.appendChild(el('div', {
      class: 'card',
      attrs: { style: `border-color: ${safety.level === 'serious' ? 'var(--danger)' : 'var(--warn)'};` },
    },
      el('strong', {}, 'Safety note'),
      el('p', {}, safety.long),
      ...(safety.resources?.map((r) => el('div', { class: 'muted' }, r)) ?? []),
    ));
  }

  type Section = 'overview' | 'schedule' | 'methods' | 'money' | 'rescue' | 'advanced';
  const section = (params.get('section') as Section | null) ?? 'overview';
  parent.appendChild(sectionTabs(habit, section));

  if (section === 'overview') {
    parent.appendChild(planSummaryCard(habit, plan, planState));
    const events = await eventsStore.byHabit(habit.id);
    const confidence = getState().meta.onboarding_metadata?.confidence;
    const suggestion = suggestPlanAdjustment({ plan, planState, events, ...(confidence !== undefined ? { confidence } : {}) });
    if (suggestion && suggestion.kind !== 'hold_steady') {
      const card = el('div', { class: 'card', attrs: { style: 'border-color:var(--accent-soft);' } },
        el('div', { class: 'eyebrow' }, 'Suggested adjustment'),
        el('h2', { attrs: { style: 'margin:0;font-size:1rem;' } }, suggestion.headline),
        el('p', { class: 'muted', attrs: { style: 'margin:0.35rem 0 0.5rem;font-size:0.9rem;' } }, suggestion.evidence),
      );
      if (suggestion.apply) {
        card.appendChild(el('button', {
          class: 'btn primary', attrs: { type: 'button' },
          onclick: async () => {
            await suggestion.apply!();
            toast('Target updated.');
            window.dispatchEvent(new CustomEvent('wean:state-changed'));
          },
        }, 'Apply'));
      }
      parent.appendChild(card);
    }
    parent.appendChild(planConfidenceCard(plan));
    parent.appendChild(habitEditCard(habit, st.settings.motivationAnchor));
  } else if (section === 'schedule') {
    parent.appendChild(planSummaryCard(habit, plan, planState));
    parent.appendChild(lifecycleCard(plan));
    parent.appendChild(exceptionCard(plan));
    parent.appendChild(hybridCard(planState));
    parent.appendChild(resetCard(planState));
  } else if (section === 'methods') {
    parent.appendChild(await methodsCard(habit));
  } else if (section === 'money') {
    parent.appendChild(await moneyCard(habit, planState));
    parent.appendChild(await templatesCard(habit));
  } else if (section === 'rescue') {
    parent.appendChild(triggersCard(st.settings));
    parent.appendChild(favoritesCard(st.settings));
  } else {
    parent.appendChild(paceCard(plan));
    parent.appendChild(slipCard(plan));
    parent.appendChild(dangerZoneCard(habit, st.meta.active_habit_id));
  }
}

function sectionTabs(habit: HabitRecord, current: string): HTMLElement {
  const sections: Array<{ key: string; label: string }> = [
    { key: 'overview', label: 'Overview' },
    { key: 'schedule', label: 'Schedule' },
    { key: 'methods', label: 'Methods' },
    { key: 'money', label: 'Money' },
    { key: 'rescue', label: 'Rescue' },
    { key: 'advanced', label: 'Advanced' },
  ];
  const row = el('div', {
    class: 'tabs-scroll',
    attrs: { role: 'tablist', style: 'margin-bottom:0.75rem;white-space:nowrap;' },
  });
  for (const s of sections) {
    const isActive = current === s.key;
    const tag = el('button', {
      class: `tag${isActive ? ' active' : ''}`,
      attrs: {
        type: 'button',
        role: 'tab',
        'aria-selected': isActive ? 'true' : 'false',
        ...(isActive ? { 'aria-current': 'true' } : {}),
      },
      onclick: () => navigate('/plan', { habit: habit.id, section: s.key }),
    }, s.label);
    row.appendChild(tag);
  }
  return row;
}

function planConfidenceCard(plan: PlanRecord): HTMLElement {
  // Persist confidence on onboarding_metadata so adaptation.ts reads it the
  // next time a suggestion is generated.
  const current = getState().meta.onboarding_metadata?.confidence;
  return el('div', { class: 'card' },
    el('h2', {}, 'How is this plan feeling?'),
    el('p', { class: 'muted' }, "We use this to suggest gentler or more ambitious targets when it's time to tune."),
    el('div', { class: 'row', attrs: { style: 'flex-wrap:wrap;gap:0.5rem;' } },
      ...([1, 2, 3, 4, 5] as const).map((n) => el('button', {
        class: `btn small${current === n ? ' primary' : ' ghost'}`,
        attrs: { type: 'button', 'aria-pressed': current === n ? 'true' : 'false' },
        onclick: async () => {
          const meta = getState().meta.onboarding_metadata ?? {
            version: 2,
            quickStartCompletedAt: nowIso(),
            baselineConfidence: 'estimated' as const,
            selectedGoalStyle: 'unsure' as const,
            selectedPace: plan.pace,
            templateCategory: 'other' as const,
            tuneUpCardsSeen: [],
            tuneUpCardsCompleted: [],
          };
          await metaSet('onboarding_metadata', { ...meta, confidence: n });
          toast(`Saved confidence ${n}/5.`);
          window.dispatchEvent(new CustomEvent('wean:state-changed'));
        },
      }, `${n}/5`)),
    ),
  );
}

function favoritesCard(s: ReturnType<typeof getState>['settings']): HTMLElement {
  const favs = s.favoriteRescueModuleIds ?? [];
  const card = el('div', { class: 'card' },
    el('h2', {}, 'Favorite rescue modules'),
    el('p', { class: 'muted', attrs: { style: 'font-size:0.85rem;' } },
      'Favorites surface first in the urge command center and rescue tab.'),
  );
  if (favs.length === 0) {
    card.appendChild(el('p', { class: 'muted', attrs: { style: 'margin-top:0.5rem;' } },
      'No favorites yet. Tap "Favorite this module" on a Progress insight when one helps.'));
    return card;
  }
  const list = el('div', { class: 'kbd-list', attrs: { style: 'margin-top:0.5rem;flex-wrap:wrap;' } });
  for (const id of favs) {
    const niceName = id.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    const tag = el('button', {
      class: 'tag', attrs: { type: 'button', 'aria-label': `Remove ${niceName} favorite` },
    }, `${niceName} ✕`);
    tag.onclick = async () => {
      const cur = await settingsStore.get();
      const remaining = (cur.favoriteRescueModuleIds ?? []).filter((m) => m !== id);
      await settingsStore.set({ ...cur, favoriteRescueModuleIds: remaining });
      toast('Removed from favorites.');
      window.dispatchEvent(new CustomEvent('wean:state-changed'));
    };
    list.appendChild(tag);
  }
  card.appendChild(list);
  return card;
}

function lifecycleCard(plan: PlanRecord): HTMLElement {
  const state = effectiveLifecycle(plan);
  const paused = state === 'paused';
  const graduated = state === 'graduated';
  const card = el('div', { class: 'card' },
    el('h2', {}, 'Plan state'),
    el('div', {}, `Current: ${lifecycleLabel(state)}.`),
  );
  if (paused && plan.pausedUntil) {
    card.appendChild(el('p', { class: 'muted' },
      `Paused until ${new Date(plan.pausedUntil).toLocaleString()}.`));
  } else if (paused) {
    card.appendChild(el('p', { class: 'muted' }, 'Paused indefinitely.'));
  } else if (graduated) {
    card.appendChild(el('p', { class: 'muted' }, 'You hit this goal. Rescue still available any time.'));
  }
  const row = el('div', { class: 'stack', attrs: { style: 'margin-top:0.5rem;' } });
  if (paused) {
    row.appendChild(el('button', {
      class: 'btn primary block', attrs: { type: 'button' },
      onclick: async () => {
        await resumePlan(plan);
        toast('Plan resumed.');
        window.dispatchEvent(new CustomEvent('wean:state-changed'));
      },
    }, 'Resume plan'));
  } else {
    row.appendChild(el('button', {
      class: 'btn block', attrs: { type: 'button' },
      onclick: async () => {
        const daysStr = prompt('Pause for how many days? (Leave blank for indefinite.)', '');
        if (daysStr === null) return;
        let until: string | undefined;
        const trimmed = daysStr.trim();
        if (trimmed) {
          const n = Number(trimmed);
          if (!Number.isFinite(n) || n <= 0) {
            toast('Enter a positive number of days, or leave blank.');
            return;
          }
          until = new Date(Date.now() + n * 86400000).toISOString();
        }
        await pausePlan(plan, until);
        const days = until ? Math.round((new Date(until).getTime() - Date.now()) / 86400000) : 0;
        toast(until ? `Paused for ${days} day${days === 1 ? '' : 's'}.` : 'Paused.');
        window.dispatchEvent(new CustomEvent('wean:state-changed'));
      },
    }, 'Pause plan'));
  }
  if (!graduated) {
    row.appendChild(el('button', {
      class: 'btn block', attrs: { type: 'button' },
      onclick: async () => {
        await moveToMaintenance(plan);
        toast('Moved to maintenance.');
        window.dispatchEvent(new CustomEvent('wean:state-changed'));
      },
    }, 'Move to maintenance'));
    row.appendChild(el('button', {
      class: 'btn block', attrs: { type: 'button' },
      onclick: async () => {
        if (!confirm('Mark this plan as graduated? Rescue stays available; logging continues.')) return;
        await graduatePlan(plan);
        toast('Graduated — well done.');
        window.dispatchEvent(new CustomEvent('wean:state-changed'));
      },
    }, 'Graduate plan'));
  }
  card.appendChild(row);
  return card;
}

function exceptionCard(plan: PlanRecord): HTMLElement {
  let chosen: PlanExceptionKind | undefined;
  let note = '';
  const card = el('div', { class: 'card' },
    el('h2', {}, 'Log an exception'),
    el('p', { class: 'muted' }, 'An exception is not quitting. It helps the plan stay realistic.'),
  );
  const list = el('div', { class: 'choice-list' });
  for (const opt of EXCEPTION_OPTIONS) {
    const c = el('button', {
      class: 'choice', attrs: { type: 'button' },
      onclick: () => {
        chosen = opt.value;
        for (const child of [...list.children]) {
          (child as HTMLElement).classList.toggle('selected', (child as HTMLElement).dataset['kind'] === opt.value);
        }
      },
    }, opt.label);
    c.dataset['kind'] = opt.value;
    list.appendChild(c);
  }
  card.appendChild(list);
  card.appendChild(el('div', { class: 'field' },
    el('label', {}, 'Optional note'),
    el('input', { placeholder: 'e.g. wedding weekend',
      oninput: (e: Event) => { note = (e.target as HTMLInputElement).value.trim(); } }),
  ));
  card.appendChild(el('button', {
    class: 'btn primary', attrs: { type: 'button' },
    onclick: async () => {
      if (!chosen) { toast('Pick a kind first.'); return; }
      const kindForLog = chosen;
      await logPlanException({ plan, kind: kindForLog, ...(note ? { note } : {}) });
      const crisis = containsCrisisLanguage(note);
      toast('Exception logged.');
      if (crisis) {
        toast('Support is available. Opening resources.', 6000);
        navigate('/resources');
        return;
      }
      window.dispatchEvent(new CustomEvent('wean:state-changed'));
    },
  }, 'Log exception'));
  return card;
}

function dangerZoneCard(habit: HabitRecord, defaultHabitId: string | undefined): HTMLElement {
  // Isolate destructive actions in a single, clearly framed card.
  return el('div', {
    class: 'card',
    attrs: { style: 'border-color:var(--danger);' },
  },
    el('h2', {}, 'Danger zone'),
    el('p', { class: 'muted' }, 'These actions change or remove this plan. Logs are kept.'),
    el('div', { class: 'stack', attrs: { style: 'margin-top:0.5rem;' } },
      defaultHabitId === habit.id
        ? el('span', { class: 'muted' }, 'This is the default plan.')
        : el('button', {
            class: 'btn', attrs: { type: 'button' },
            onclick: async () => {
              await metaSet('active_habit_id', habit.id);
              const st = getState();
              const { plan } = planFor(habit.id, st);
              if (plan) await metaSet('active_plan_id', plan.id);
              toast('Set as default.');
              window.dispatchEvent(new CustomEvent('wean:state-changed'));
            },
          }, 'Make default'),
      el('button', {
        class: 'btn danger', attrs: { type: 'button' },
        onclick: async (e: Event) => {
          const btn = e.currentTarget as HTMLButtonElement;
          if (btn.disabled) return;
          const habits2 = activeHabits();
          if (habits2.length <= 1) { toast('Add another plan first.'); return; }
          const labelInConfirm = getState().settings.privacyMode ? 'this plan' : `"${habit.privateLabel ?? habit.label}"`;
          if (!confirm(`Archive ${labelInConfirm}? Logging stops; history is preserved.`)) return;
          btn.disabled = true;
          try {
            await habits.put({ ...habit, active: false, archivedAt: nowIso(), updatedAt: nowIso() });
            const st = getState();
            if (st.meta.active_habit_id === habit.id) {
              const next = activeHabits(st).find((h) => h.id !== habit.id);
              if (next) {
                await metaSet('active_habit_id', next.id);
                const { plan } = planFor(next.id, st);
                if (plan) await metaSet('active_plan_id', plan.id);
              }
            }
            toast('Archived.');
            navigate('/today');
            window.dispatchEvent(new CustomEvent('wean:state-changed'));
          } catch (err) {
            console.error(err);
            toast('Could not archive — try again.');
            btn.disabled = false;
          }
        },
      }, 'Archive plan'),
    ),
  );
}

function habitSwitcher(allHabits: HabitRecord[], current: HabitRecord): HTMLElement {
  const wrap = el('div', { class: 'card' },
    el('div', { attrs: { style: 'display:flex; align-items:center; justify-content:space-between; gap:0.5rem;' } },
      el('h2', { attrs: { style: 'margin:0;' } }, 'Plans'),
      el('a', { href: '#/plans', class: 'tag' }, 'Manage all'),
    ),
    el('div', { class: 'kbd-list', attrs: { style: 'margin-top:0.5rem;' } },
      ...allHabits.map((h) => {
        const isActive = h.id === current.id;
        const t = el('button', {
          class: `tag${isActive ? ' active' : ''}`,
          attrs: { type: 'button', ...(isActive ? { 'aria-current': 'true' } : {}) },
          onclick: () => navigate('/plan', { habit: h.id }),
        }, habitDisplayLabel(h));
        return t;
      }),
      el('button', { class: 'tag', attrs: { type: 'button' }, onclick: () => navigate('/onboard') }, '+ New plan'),
    ),
  );
  return wrap;
}

function planSummaryCard(habit: HabitRecord, plan: PlanRecord, ps: PlanStateRecord): HTMLElement {
  // Lead with plain-language summary; raw planType only surfaces in Advanced.
  return el('div', { class: 'card' },
    el('h2', {}, 'Plan'),
    el('div', {}, `Working on: ${habitDisplayLabel(habit)}.`),
    el('div', {}, planTargetLine(ps.state)),
    el('div', { class: 'muted', attrs: { style: 'font-size:0.85rem;' } },
      ps.state.lastExplanation ?? 'No adjustments yet.'),
    plan.lifecycleState && plan.lifecycleState !== 'active'
      ? el('div', { class: 'muted', attrs: { style: 'font-size:0.85rem;margin-top:0.35rem;' } },
          `State: ${plan.lifecycleState}.`)
      : null,
  );
}

function habitEditCard(habit: HabitRecord, motivation: string | undefined): HTMLElement {
  return el('div', { class: 'card' },
    el('h2', {}, 'Edit plan'),
    el('div', { class: 'field' },
      el('label', {}, 'Label'),
      el('input', { value: habit.label, onchange: async (e: Event) => {
        await habits.put({ ...habit, label: (e.target as HTMLInputElement).value, updatedAt: nowIso() });
        toast('Saved.'); window.dispatchEvent(new CustomEvent('wean:state-changed'));
      } })),
    el('div', { class: 'field' },
      el('label', {}, 'Private alias (optional)'),
      el('input', { value: habit.privateLabel ?? '', onchange: async (e: Event) => {
        const v = (e.target as HTMLInputElement).value.trim();
        await habits.put({ ...habit, privateLabel: v || undefined, updatedAt: nowIso() });
        toast('Saved.'); window.dispatchEvent(new CustomEvent('wean:state-changed'));
      } })),
    el('div', { class: 'field' },
      el('label', {}, 'Unit (singular)'),
      el('input', { value: habit.unitSingular, onchange: async (e: Event) => {
        await habits.put({ ...habit, unitSingular: (e.target as HTMLInputElement).value, updatedAt: nowIso() });
        toast('Saved.'); window.dispatchEvent(new CustomEvent('wean:state-changed'));
      } })),
    el('div', { class: 'field' },
      el('label', {}, 'Unit (plural)'),
      el('input', { value: habit.unitPlural, onchange: async (e: Event) => {
        await habits.put({ ...habit, unitPlural: (e.target as HTMLInputElement).value, updatedAt: nowIso() });
        toast('Saved.'); window.dispatchEvent(new CustomEvent('wean:state-changed'));
      } })),
    el('div', { class: 'field' },
      el('label', {}, 'Motivation anchor (shared)'),
      el('textarea', { rows: 2, value: motivation ?? '',
        onchange: async (e: Event) => {
          const cur = await settingsStore.get();
          await settingsStore.set({ ...cur, motivationAnchor: (e.target as HTMLTextAreaElement).value.trim() || undefined });
          toast('Saved.'); window.dispatchEvent(new CustomEvent('wean:state-changed'));
        } })),
  );
}

async function methodsCard(habit: HabitRecord): Promise<HTMLElement> {
  const card = el('div', { class: 'card' }, el('h2', {}, 'Methods'));
  const list = el('div', { class: 'stack', attrs: { style: 'gap:6px;' } });
  card.appendChild(list);
  const adder = el('div', { class: 'stack', attrs: { style: 'margin-top:0.5rem;' } });
  card.appendChild(adder);
  await rebuild();
  return card;

  async function rebuild(): Promise<void> {
    while (list.firstChild) list.removeChild(list.firstChild);
    while (adder.firstChild) adder.removeChild(adder.firstChild);
    const all = (await consumptionMethods.byHabit(habit.id)).sort((a, b) => a.sortOrder - b.sortOrder);
    if (all.length === 0) {
      list.appendChild(el('p', { class: 'muted' }, 'No methods yet. Add one below.'));
    }
    for (const m of all) {
      list.appendChild(el('div', { class: 'list-row' },
        el('div', {},
          el('div', { class: 'label' }, `${m.label}${!m.active ? ' · archived' : ''}`),
          el('div', { class: 'sub' }, `${m.defaultLogMode.replace('_', ' ')} · ${m.unitPlural}`),
        ),
        el('button', { class: 'btn ghost', onclick: async () => {
          await consumptionMethods.put({ ...m, active: !m.active, updatedAt: nowIso() });
          toast(m.active ? 'Method archived.' : 'Method restored.');
          await rebuild();
          window.dispatchEvent(new CustomEvent('wean:state-changed'));
        } }, m.active ? 'Archive' : 'Restore'),
      ));
    }
    // Adder
    const domain = habit.domain ?? CATEGORY_TO_DOMAIN[habit.category] ?? 'other';
    const seeds = DEFAULT_METHODS_BY_DOMAIN[domain] ?? [];
    const existingKinds = new Set(all.map((m) => m.kind));
    const remaining = seeds.filter((s) => !existingKinds.has(s.kind));
    if (remaining.length > 0) {
      const row = el('div', { class: 'kbd-list' });
      for (const s of remaining) {
        row.appendChild(el('button', { class: 'tag', attrs: { type: 'button' }, onclick: async () => {
          await consumptionMethods.put(buildMethodFromSeed(habit, domain, s));
          toast(`Added ${s.label}.`);
          await rebuild();
          window.dispatchEvent(new CustomEvent('wean:state-changed'));
        } }, `+ ${s.label}`));
      }
      adder.appendChild(row);
    }
    // Custom
    const labelInput = el('input', { placeholder: 'Custom method label', attrs: { style: 'flex:1;' } });
    const modeSelect = el('select') as HTMLSelectElement;
    for (const mode of ['instant', 'session', 'extended_session'] as MethodLogMode[]) {
      const op = el('option', { value: mode }, mode);
      modeSelect.appendChild(op);
    }
    adder.appendChild(el('div', { class: 'row', attrs: { style: 'margin-top:0.5rem;' } },
      labelInput, modeSelect,
      el('button', { class: 'btn primary', onclick: async () => {
        const label = (labelInput as HTMLInputElement).value.trim();
        if (!label) { toast('Enter a label.'); return; }
        const mode = modeSelect.value as MethodLogMode;
        const seed = {
          kind: label.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 32) || `method_${Date.now()}`,
          label,
          unitSingular: label.toLowerCase(),
          unitPlural: `${label.toLowerCase()}s`,
          defaultLogMode: mode,
          defaultDomainUnits: 1,
          sortOrder: 100,
          ...(mode === 'session' ? { defaultSessionMinutes: 5, mergeWindowMinutes: 10 } : {}),
          ...(mode === 'extended_session' ? { defaultSessionMinutes: 45, mergeWindowMinutes: 45 } : {}),
        };
        await consumptionMethods.put(buildMethodFromSeed(habit, domain, seed));
        (labelInput as HTMLInputElement).value = '';
        toast(`Added ${label}.`);
        await rebuild();
        window.dispatchEvent(new CustomEvent('wean:state-changed'));
      } }, 'Add'),
    ));
  }
}

async function templatesCard(habit: HabitRecord): Promise<HTMLElement> {
  const card = el('div', { class: 'card' }, el('h2', {}, 'Purchase templates'));
  const list = el('div', { class: 'stack', attrs: { style: 'gap:6px;' } });
  card.appendChild(list);
  const adder = el('div', { attrs: { style: 'margin-top:0.5rem;' } });
  card.appendChild(adder);
  await rebuild();
  return card;

  async function rebuild(): Promise<void> {
    while (list.firstChild) list.removeChild(list.firstChild);
    while (adder.firstChild) adder.removeChild(adder.firstChild);
    const all = await purchaseTemplates.byHabit(habit.id);
    if (all.length === 0) {
      list.appendChild(el('p', { class: 'muted' }, 'No templates yet. Templates feed savings estimates.'));
    }
    for (const t of all) {
      const subscription = t.kind === 'subscription';
      const right = subscription && t.recurringAmount
        ? `${t.currency} ${t.recurringAmount.toFixed(2)}/${t.recurrence ?? 'month'}`
        : t.defaultPrice
          ? `${t.currency} ${t.defaultPrice.toFixed(2)}`
          : t.baselineSpendPerPeriod
            ? `${t.currency} ${t.baselineSpendPerPeriod.amount.toFixed(2)}/${t.baselineSpendPerPeriod.period}`
            : '—';
      const modeLabel: Record<SavingsMode, string> = {
        unit_avoidance: 'Per-use savings',
        purchase_cadence: 'Purchase cadence',
        period_spend: 'Period spending',
      };
      const subLine = subscription
        ? `subscription · ${right}${t.canceledAt ? ' · canceled' : ''}`
        : `${modeLabel[t.savingsMode]} · ${right} · ${t.confidence} confidence`;
      const actions = el('div', { class: 'row', attrs: { style: 'gap:0.4rem;flex-wrap:wrap;' } });
      if (subscription && !t.canceledAt) {
        actions.appendChild(el('button', {
          class: 'btn ghost', attrs: { type: 'button' },
          onclick: async () => {
            await purchaseTemplates.put({ ...t, canceledAt: nowIso(), active: false, updatedAt: nowIso() });
            toast('Subscription canceled — money protected.');
            await rebuild();
            window.dispatchEvent(new CustomEvent('wean:state-changed'));
          },
        }, 'Mark canceled'));
      }
      actions.appendChild(el('button', {
        class: 'btn ghost', attrs: { type: 'button' },
        onclick: async () => {
          await purchaseTemplates.put({ ...t, active: !t.active, updatedAt: nowIso() });
          toast(t.active ? 'Template archived.' : 'Template restored.');
          await rebuild();
          window.dispatchEvent(new CustomEvent('wean:state-changed'));
        },
      }, t.active ? 'Archive' : 'Restore'));
      list.appendChild(el('div', { class: 'list-row' },
        el('div', {},
          el('div', { class: 'label' }, `${t.label}${!t.active ? ' · archived' : ''}`),
          el('div', { class: 'sub' }, subLine),
        ),
        actions,
      ));
    }

    // Add form
    const labelInput = el('input', { placeholder: 'Label (e.g. Disposable vape, Streaming sub)' });
    const priceInput = el('input', { type: 'number', step: '0.01', min: '0', placeholder: 'Default price / amount' });
    const cadenceInput = el('input', { type: 'number', min: '1', placeholder: 'Days per purchase (cadence mode)' });
    const modeSelect = el('select') as HTMLSelectElement;
    const MODE_LABELS: Record<SavingsMode, string> = {
      unit_avoidance: 'Per-use savings',
      purchase_cadence: 'Purchase cadence',
      period_spend: 'Period spending',
    };
    for (const mode of ['unit_avoidance', 'purchase_cadence', 'period_spend'] as SavingsMode[]) {
      modeSelect.appendChild(el('option', { value: mode }, MODE_LABELS[mode]));
    }
    const kindSelect = el('select') as HTMLSelectElement;
    for (const k of ['purchase', 'subscription'] as const) {
      kindSelect.appendChild(el('option', { value: k }, k));
    }
    const recurrenceSelect = el('select') as HTMLSelectElement;
    for (const r of ['monthly', 'yearly', 'weekly'] as const) {
      recurrenceSelect.appendChild(el('option', { value: r }, r));
    }
    adder.appendChild(el('div', { class: 'stack' },
      el('div', { class: 'field' }, el('label', {}, 'Label'), labelInput),
      el('div', { class: 'field' }, el('label', {}, 'Kind'), kindSelect),
      el('div', { class: 'field' }, el('label', {}, 'Default price / amount'), priceInput),
      el('div', { class: 'field' }, el('label', {}, 'Recurrence (subscription only)'), recurrenceSelect),
      el('div', { class: 'field' }, el('label', {}, 'Days per purchase (cadence mode)'), cadenceInput),
      el('div', { class: 'field' }, el('label', {}, 'Savings mode'), modeSelect),
      el('button', { class: 'btn primary', onclick: async () => {
        const label = (labelInput as HTMLInputElement).value.trim();
        if (!label) { toast('Enter a label.'); return; }
        const price = Number((priceInput as HTMLInputElement).value);
        const cadence = Number((cadenceInput as HTMLInputElement).value);
        const mode = modeSelect.value as SavingsMode;
        const kind = kindSelect.value as 'purchase' | 'subscription';
        const recurrence = recurrenceSelect.value as 'monthly' | 'yearly' | 'weekly';
        const cur = (await moneyModels.get(habit.id))?.currency ?? 'USD';
        const tpl: PurchaseTemplateRecord = {
          id: uuid(), schemaVersion: 2, habitId: habit.id,
          label, currency: cur,
          savingsMode: mode,
          confidence: mode === 'unit_avoidance' ? 'high' : mode === 'purchase_cadence' ? 'medium' : 'low',
          active: true,
          kind,
          createdAt: nowIso(), updatedAt: nowIso(),
        };
        if (kind === 'subscription') {
          tpl.recurrence = recurrence;
          if (price > 0) tpl.recurringAmount = price;
        } else {
          if (price > 0) tpl.defaultPrice = price;
          if (cadence > 0) tpl.baselineDaysPerPurchase = cadence;
        }
        await purchaseTemplates.put(tpl);
        (labelInput as HTMLInputElement).value = '';
        (priceInput as HTMLInputElement).value = '';
        (cadenceInput as HTMLInputElement).value = '';
        toast(kind === 'subscription' ? 'Subscription added.' : 'Template added.');
        await rebuild();
        window.dispatchEvent(new CustomEvent('wean:state-changed'));
      } }, 'Add template'),
    ));
  }
}

async function moneyCard(habit: HabitRecord, ps: PlanStateRecord): Promise<HTMLElement> {
  const m = (await moneyModels.get(habit.id)) ?? blankMoney(habit.id);
  const card = el('div', { class: 'card' }, el('h2', {}, 'Cost & savings'));
  return rebuild();

  function rebuild(): HTMLElement {
    while (card.children.length > 1) card.removeChild(card.lastChild!);
    const cur = currencySymbol(m.currency);

    const enableRow = el('label', { class: 'choice' },
      el('input', { type: 'checkbox', checked: m.enabled, onchange: async (e: Event) => {
        m.enabled = (e.target as HTMLInputElement).checked;
        await moneyModels.put({ ...m, updatedAt: nowIso() });
        rebuild();
        toast(m.enabled ? 'Tracking enabled.' : 'Tracking disabled.');
      } }),
      el('div', {}, 'Track money for this plan'),
    );
    card.appendChild(enableRow);
    if (!m.enabled) return card;

    const types: Array<{ value: MoneyModelRecord['estimationType']; label: string; sub: string }> = [
      { value: 'per_use',       label: 'Per use',         sub: `${cur}X each time` },
      { value: 'per_pack',      label: 'Per pack/bundle', sub: `${cur}X for N units` },
      { value: 'weekly_spend',  label: 'Per week',        sub: `weekly ${cur}` },
      { value: 'monthly_spend', label: 'Per month',       sub: `monthly ${cur}` },
    ];
    const typeList = el('div', { class: 'choice-list', attrs: { style: 'margin-top:0.5rem;' } });
    for (const t of types) {
      typeList.appendChild(el('label', { class: `choice ${m.estimationType === t.value ? 'selected' : ''}` },
        el('input', { type: 'radio', name: `m-type-${habit.id}`, checked: m.estimationType === t.value, onchange: async () => {
          m.estimationType = t.value;
          if (t.value === 'weekly_spend') m.period = 'week';
          if (t.value === 'monthly_spend') m.period = 'month';
          await moneyModels.put({ ...m, updatedAt: nowIso() });
          rebuild();
        } }),
        el('div', {}, el('div', {}, t.label), el('div', { class: 'text-muted', attrs: { style: 'font-size:0.78rem;' } }, t.sub)),
      ));
    }
    card.appendChild(typeList);

    // Currency
    const curField = el('div', { class: 'field' },
      el('label', {}, 'Currency'),
      (() => {
        const sel = el('select', {
          onchange: async (e: Event) => {
            m.currency = (e.target as HTMLSelectElement).value;
            await moneyModels.put({ ...m, updatedAt: nowIso() });
            rebuild();
          },
        }) as HTMLSelectElement;
        for (const code of ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'INR', 'BRL', 'MXN']) {
          const op = el('option', { value: code }, code);
          if ((m.currency ?? 'USD') === code) op.selected = true;
          sel.appendChild(op);
        }
        return sel;
      })(),
    );
    card.appendChild(curField);

    if (m.estimationType === 'per_use') {
      card.appendChild(numField(`Cost per use (${cur})`, m.costPerUse, async (n) => { m.costPerUse = n; await save(); }));
    } else if (m.estimationType === 'per_pack') {
      card.appendChild(numField(`Pack/bundle cost (${cur})`, m.purchaseCost, async (n) => { m.purchaseCost = n; await save(); }));
      card.appendChild(numField(`Uses per pack`, m.unitsPerPurchase, async (n) => { m.unitsPerPurchase = n; await save(); }));
    } else {
      card.appendChild(numField(
        m.estimationType === 'weekly_spend' ? `Weekly spend (${cur})` : `Monthly spend (${cur})`,
        m.estimatedSpendPerPeriod,
        async (n) => { m.estimatedSpendPerPeriod = n; await save(); }
      ));
    }

    // Spending cap (gambling/shopping/gaming guardrail)
    card.appendChild(el('h2', { attrs: { style: 'margin-top:0.75rem;font-size:1rem;' } }, 'Spending limit'));
    card.appendChild(el('p', { class: 'muted', attrs: { style: 'font-size:0.85rem;' } },
      'Optional. Set a weekly or monthly spending cap. Wean warns when you cross the threshold.'));
    const capPeriodSel = el('select', {
      onchange: async (e: Event) => {
        const v = (e.target as HTMLSelectElement).value;
        m.spendingCapPeriod = v === '' ? undefined : (v as 'week' | 'month');
        if (m.spendingCapPeriod === undefined) m.spendingCap = undefined;
        await save();
      },
    }) as HTMLSelectElement;
    for (const opt of [{ v: '', l: 'Off' }, { v: 'week', l: 'Per week' }, { v: 'month', l: 'Per month' }]) {
      const op = el('option', { value: opt.v }, opt.l);
      if ((m.spendingCapPeriod ?? '') === opt.v) op.selected = true;
      capPeriodSel.appendChild(op);
    }
    card.appendChild(el('div', { class: 'field' }, el('label', {}, 'Cap period'), capPeriodSel));
    if (m.spendingCapPeriod) {
      card.appendChild(numField(`Cap amount (${cur})`, m.spendingCap, async (n) => { m.spendingCap = n; await save(); }));
      card.appendChild(numField('Warn at percent', m.spendingCapWarnPct ?? 80, async (n) => {
        m.spendingCapWarnPct = n >= 0 && n <= 100 ? n : 80; await save();
      }));
    }

    // Live preview
    let usesPerWeek: number | undefined;
    const s = ps.state;
    if (s.planType === 'interval') usesPerWeek = ((16 * 60 * 60_000) / s.baselineIntervalMs) * 7;
    else if (s.planType === 'quota') usesPerWeek = s.period === 'daily' ? s.baselineQuota * 7 : s.period === 'weekly' ? s.baselineQuota : s.baselineQuota / 4.33;
    const cpu = costPerUse(m, { baselineUsesPerWeek: usesPerWeek });
    card.appendChild(el('p', { class: 'muted', attrs: { style: 'margin: 0.25rem 0 0;' } },
      cpu > 0
        ? `≈ ${cur}${cpu.toFixed(2)} per use · ${cur}${(cpu * (usesPerWeek ?? 7)).toFixed(2)} per week at baseline pace.`
        : 'Fill the fields above to see an estimate.',
    ));

    return card;
  }

  async function save() {
    await moneyModels.put({ ...m, updatedAt: nowIso() });
    toast('Saved.');
    window.dispatchEvent(new CustomEvent('wean:state-changed'));
  }
  function numField(label: string, value: number | undefined, setter: (n: number) => Promise<void> | void): HTMLElement {
    return el('div', { class: 'field' },
      el('label', {}, label),
      el('input', {
        type: 'number', step: '0.01', min: '0',
        value: value === undefined ? '' : String(value),
        onchange: async (e: Event) => {
          const n = Number((e.target as HTMLInputElement).value);
          await setter(isNaN(n) ? 0 : n);
        },
      }),
    );
  }
}

function blankMoney(habitId: string): MoneyModelRecord {
  return {
    habitId, enabled: false, currency: 'USD', estimationType: 'per_use',
    labelAsEstimate: true, updatedAt: nowIso(),
  };
}

function paceCard(plan: PlanRecord): HTMLElement {
  const LABELS: Record<Pace, { label: string; hint: string }> = {
    gentle:    { label: 'Gentle',    hint: 'Smaller weekly steps. Lower miss risk.' },
    steady:    { label: 'Steady',    hint: 'Balanced progression. Recommended.' },
    ambitious: { label: 'Ambitious', hint: 'Faster stretches when wins stack up.' },
  };
  return el('div', { class: 'card' },
    el('h2', {}, 'Pace'),
    el('div', { class: 'choice-list' },
      ...(['gentle', 'steady', 'ambitious'] as Pace[]).map((p) =>
        el('label', { class: `choice ${plan.pace === p ? 'selected' : ''}` },
          el('input', {
            type: 'radio', name: `pace-${plan.id}`, checked: plan.pace === p,
            onchange: async () => { await plans.put({ ...plan, pace: p, updatedAt: nowIso() }); toast('Pace updated.'); window.dispatchEvent(new CustomEvent('wean:state-changed')); },
          }),
          el('div', {},
            el('div', {}, LABELS[p].label),
            el('div', { class: 'text-muted', attrs: { style: 'font-size:0.8rem;' } }, LABELS[p].hint),
          ),
        )
      ),
    ),
  );
}

function slipCard(plan: PlanRecord): HTMLElement {
  const LABELS: Record<SlipStyle, { label: string; hint: string }> = {
    gentle:   { label: 'Gentle',      hint: 'Soft language. Easy to come back.' },
    balanced: { label: 'Reflective',  hint: 'A short pause to learn from it, no shame.' },
    direct:   { label: 'Structured',  hint: 'Clear next steps and structure to recover.' },
  };
  return el('div', { class: 'card' },
    el('h2', {}, 'Recovery style'),
    el('p', { class: 'text-muted', attrs: { style: 'margin-top:-0.25rem;font-size:0.85rem;' } },
      'How Wean responds when you slip. Not a grade.'),
    el('div', { class: 'choice-list' },
      ...(['gentle', 'balanced', 'direct'] as SlipStyle[]).map((p) =>
        el('label', { class: `choice ${plan.slipStyle === p ? 'selected' : ''}` },
          el('input', {
            type: 'radio', name: `slip-${plan.id}`, checked: plan.slipStyle === p,
            onchange: async () => { await plans.put({ ...plan, slipStyle: p, updatedAt: nowIso() }); toast('Recovery style updated.'); window.dispatchEvent(new CustomEvent('wean:state-changed')); },
          }),
          el('div', {},
            el('div', {}, LABELS[p].label),
            el('div', { class: 'text-muted', attrs: { style: 'font-size:0.8rem;' } }, LABELS[p].hint),
          ),
        )
      ),
    ),
  );
}

function triggersCard(s: ReturnType<typeof getState>['settings']): HTMLElement {
  const TRIGGER_OPTIONS = ['stress', 'boredom', 'anxiety', 'loneliness', 'habit', 'after_work', 'after_meals', 'before_bed', 'waking_up', 'driving', 'social', 'celebration', 'anger', 'sadness', 'discomfort'];
  const triggerSet = new Set(s.triggers ?? []);
  const card = el('div', { class: 'card' },
    el('h2', {}, 'Triggers (shared)'),
    el('p', { class: 'muted', attrs: { style: 'font-size:0.8rem;' } }, 'Triggers bias rescue recommendations across all plans.'),
  );
  const row = el('div', { class: 'kbd-list' });
  for (const t of TRIGGER_OPTIONS) {
    const tag = el('button', {
      class: `tag${triggerSet.has(t) ? ' active' : ''}`,
      attrs: { type: 'button', 'aria-pressed': triggerSet.has(t) ? 'true' : 'false' },
    }, t.replace(/_/g, ' '));
    tag.onclick = async () => {
      const wasOn = triggerSet.has(t);
      if (wasOn) { triggerSet.delete(t); tag.classList.remove('active'); tag.setAttribute('aria-pressed', 'false'); }
      else { triggerSet.add(t); tag.classList.add('active'); tag.setAttribute('aria-pressed', 'true'); }
      const cur = await settingsStore.get();
      await settingsStore.set({ ...cur, triggers: [...triggerSet] });
      toast('Saved.');
      window.dispatchEvent(new CustomEvent('wean:state-changed'));
    };
    row.appendChild(tag);
  }
  card.appendChild(row);
  return card;
}

function hybridCard(ps: PlanStateRecord): HTMLElement {
  const sq = ps.secondaryQuota;
  if (sq) {
    return el('div', { class: 'card' },
      el('h2', {}, 'Hybrid: weekly cap'),
      el('div', { class: 'stack' },
        el('div', {}, `Cap: ${sq.currentQuota} per week (used ${sq.usedThisPeriod}).`),
        el('button', { class: 'btn ghost', onclick: async () => {
          await planStates.put({ ...ps, secondaryQuota: undefined, updatedAt: nowIso() });
          toast('Weekly cap removed.');
          window.dispatchEvent(new CustomEvent('wean:state-changed'));
        } }, 'Remove weekly cap'),
      ),
    );
  }
  // Closure-held refs avoid the document.getElementById id-collision hazard
  // when the same plan renders twice (e.g. background + foreground tab).
  const baselineInput = el('input', { type: 'number', min: '1', value: '14' }) as HTMLInputElement;
  const startInput = el('input', { type: 'number', min: '1', value: '10' }) as HTMLInputElement;
  return el('div', { class: 'card' },
    el('h2', {}, 'Hybrid: weekly cap'),
    el('div', { class: 'stack' },
      el('p', { class: 'muted' }, 'Layer a weekly maximum on top of this plan.'),
      el('div', { class: 'row' }, baselineInput, startInput),
      el('button', { class: 'btn primary', onclick: async () => {
        const baseline = Number(baselineInput.value);
        const start = Number(startInput.value);
        if (!Number.isFinite(baseline) || baseline < 1 || !Number.isFinite(start) || start < 1) {
          toast('Enter positive baseline and starting cap.');
          return;
        }
        if (start > baseline) { toast('Starting cap should be ≤ baseline.'); return; }
        await planStates.put({ ...ps, secondaryQuota: newWeeklyCap(baseline, start), updatedAt: nowIso() });
        toast('Weekly cap added.');
        window.dispatchEvent(new CustomEvent('wean:state-changed'));
      } }, 'Add weekly cap'),
    ),
  );
}

function resetCard(ps: PlanStateRecord): HTMLElement {
  return el('div', { class: 'card' },
    el('h2', {}, 'Reset progression'),
    el('button', { class: 'btn danger', onclick: async () => {
      if (!confirm('Reset plan progression to starting target? Events are kept.')) return;
      let s = ps.state;
      if (s.planType === 'interval') {
        s = { ...s, targetIntervalMs: s.baselineIntervalMs, successStreak: 0, slipStreak: 0, lastUseAt: undefined };
      } else if (s.planType === 'window') {
        s = { ...s, targetFirstUseMinutes: s.baselineFirstUseMinutes, successDays: 0, missDays: 0, todayFirstUseMinutes: undefined };
      } else {
        s = { ...s, currentQuota: s.baselineQuota, usedThisPeriod: 0, successPeriods: 0 };
      }
      await planStates.put({ ...ps, state: s, updatedAt: nowIso() });
      toast('Plan reset.');
      window.dispatchEvent(new CustomEvent('wean:state-changed'));
    } }, 'Reset'),
  );
}

function planTargetLine(s: PlanState): string {
  if (s.planType === 'interval') {
    const i = s as IntervalState;
    return `Current target: wait ${formatDuration(i.targetIntervalMs)} between uses (baseline ${formatDuration(i.baselineIntervalMs)}).`;
  }
  if (s.planType === 'window') {
    const w = s as WindowState;
    return `Target first-use after ${formatHm(w.targetFirstUseMinutes)} (baseline ${formatHm(w.baselineFirstUseMinutes)}).`;
  }
  const q = s as QuotaState;
  return `Quota: ${q.currentQuota} per ${q.period} (baseline ${q.baselineQuota}).`;
}
